<template>
  <div class="slider" ref="slider" @click="onBarClick">
    <!-- track -->
    <div class="slider-track"></div>
    <!-- selected range -->
    <div class="slider-range" :style="rangeStyle"></div>

    <!-- handles -->
    <div
      v-for="(val, i) in values"
      :key="i"
      class="slider-handle"
      :style="handleStyle(val)"
      @mousedown.prevent="startDrag(i, $event)"
      @touchstart.prevent="startDrag(i, $event)"
      v-tooltip="Math.round(val) + '%'"
    >
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  modelValue: { type: Array, default: () => [0, 100] },
  min: { type: Number, default: 0 },
  max: { type: Number, default: 100 },
})

const emit = defineEmits(['update:modelValue'])

const values = ref([...props.modelValue])
watch(() => props.modelValue, v => { values.value = [...v] })

const slider = ref(null)

function handleStyle(val) {
  return { left: `${((val - props.min) / (props.max - props.min)) * 100}%` }
}

const rangeStyle = computed(() => {
  const [v1, v2] = [...values.value].sort((a, b) => a - b)
  const left = ((v1 - props.min) / (props.max - props.min)) * 100
  const right = ((v2 - props.min) / (props.max - props.min)) * 100
  return { left: left + '%', width: (right - left) + '%' }
})

let dragging = null
function startDrag(index, ev) {
  dragging = index
  document.addEventListener('mousemove', onMove)
  document.addEventListener('mouseup', stopDrag)
  document.addEventListener('touchmove', onMove)
  document.addEventListener('touchend', stopDrag)
}

function onMove(ev) {
  if (dragging === null) return
  const rect = slider.value.getBoundingClientRect()
  const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX
  let percent = (clientX - rect.left) / rect.width
  percent = Math.max(0, Math.min(1, percent))
  const newVal = props.min + percent * (props.max - props.min)

  // Prevent crossing
  if (dragging === 0) {
    values.value[0] = Math.min(newVal, values.value[1])
  } else {
    values.value[1] = Math.max(newVal, values.value[0])
  }

  emit('update:modelValue', [...values.value])
}

function stopDrag() {
  dragging = null
  document.removeEventListener('mousemove', onMove)
  document.removeEventListener('mouseup', stopDrag)
  document.removeEventListener('touchmove', onMove)
  document.removeEventListener('touchend', stopDrag)
}

function onBarClick(ev) {
  const rect = slider.value.getBoundingClientRect()
  const percent = (ev.clientX - rect.left) / rect.width
  const newVal = props.min + percent * (props.max - props.min)
  const dist0 = Math.abs(newVal - values.value[0])
  const dist1 = Math.abs(newVal - values.value[1])
  const idx = dist0 < dist1 ? 0 : 1

  // Prevent crossing
  if (idx === 0) {
    values.value[0] = Math.min(newVal, values.value[1])
  } else {
    values.value[1] = Math.max(newVal, values.value[0])
  }

  emit('update:modelValue', [...values.value])
}
</script>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.slider {
  position: relative;
  margin-left: 10px;
  width: calc(100% - 20px);
  height: 8px;
  background: transparent;
  cursor: pointer;
}

.slider-track {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  border: 1px solid var(--secondary-color);
  border-radius: 13px;
}

.slider-range {
  position: absolute;
  top: 0;
  bottom: 0;
  background: linear-gradient(90deg, #822312 0%, #CD5500 25%, #EDAE39 50%, #92C93A 75%, #00AF00 100%);
  border-radius: 3px;
}

.slider-handle {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: white;
  border: 2px solid var(--secondary-color);
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: center;

  &:active {
    cursor: grabbing;
  }
}
</style>